import { Component } from '@theme/component';

const SEARCH_SELECTORS = {
  input: '.color-search-input__field',
  card: '.color-browse-grid__card',
  dialog: '.tint-color-picker__dialog',
};

/**
 * Update a card's visibility based on both filter and search state.
 *
 * This helper mirrors the logic in `color-browse-filters.js` so that
 * search and filters compose cleanly instead of overwriting each
 * other's `style.display` state.
 *
 * @param {HTMLElement} card
 */
const updateCardVisibilityForSearch = (card) => {
  const passesFilters = card.dataset.filterVisible !== 'false';
  const matchesSearch = card.dataset.searchVisible !== 'false';

  const isVisible = passesFilters && matchesSearch;

  card.style.display = isVisible ? '' : 'none';
};

/**
 * Normalize text for case-insensitive, accent-insensitive comparison.
 *
 * @param {string} value
 * @returns {string}
 */
const normalizeText = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Determine whether a given card matches the search query.
 *
 * Matches on color reference code and display name.
 *
 * @param {HTMLElement} card
 * @param {string[]} tokens
 * @returns {boolean}
 */
const cardMatchesSearch = (card, tokens) => {
  if (!tokens.length) return true;

  const ref = card.dataset.colorRef || '';
  const name = card.dataset.colorName || '';
  const haystack = normalizeText(`${ref} ${name}`);

  if (!haystack) return false;

  return tokens.every((token) => haystack.includes(token));
};

/**
 * <color-browse-search> web component
 *
 * @extends {Component<{}>}
 */
class ColorBrowseSearch extends Component {
  constructor() {
    super();
    this.handleInput = this.debounce(this.handleInput.bind(this), 300);
  }

  connectedCallback() {
    super.connectedCallback();
    this.input = this.querySelector(SEARCH_SELECTORS.input);
    
    if (this.input) {
      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('change', this.handleInput);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    if (this.input) {
      this.input.removeEventListener('input', this.handleInput);
      this.input.removeEventListener('change', this.handleInput);
    }
  }

  /**
   * Debounce utility
   * @param {Function} func 
   * @param {number} wait 
   * @returns {Function}
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  handleInput() {
    if (!this.input) return;
    
    const query = normalizeText(this.input.value || '');
    const tokens = query ? query.split(/\s+/).filter(Boolean) : [];
    const hasSearch = tokens.length > 0;
    
    const root = this.closest('tint-color-picker');
    if (!root) return;

    const cards = root.querySelectorAll(SEARCH_SELECTORS.card);

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const matches = cardMatchesSearch(card, tokens);

      card.dataset.searchVisible = matches ? 'true' : 'false';
      updateCardVisibilityForSearch(card);
    }

    // Notify other components (like favorites) about current search state
    root.dispatchEvent(
      new CustomEvent('color-browse-query-change', {
        detail: { source: 'search', hasSearch },
        bubbles: true,
      }),
    );
  }
}

customElements.define('color-browse-search', ColorBrowseSearch);
